/**
 * ATL-1077 — make a raw-axios failure replayable by the dual-write outbox.
 *
 * The outbox (ATL-782) re-issues a failed write from `error.requestConfig`, a
 * `ReplayableRequestConfig` (`packages/api-client/src/client/index.ts:108`). Only
 * `@atlas/api-client` attached it, so every dual-write that goes through the raw axios instance in
 * `apiClient.js` — `organizations.create` among them — was refused by
 * `enqueueDualWriteFailure`'s first rule (`no-replayable-request`) and lost permanently.
 *
 * The config is built from axios' `error.config`, which is the request AS SENT (after the
 * sanitising request interceptor), so a replay re-issues exactly what failed. Headers are
 * deliberately excluded: `TypedApiClient.replayRequest` puts the request through its own auth
 * interceptor, so a persisted bearer token would be stale by the time it is used AND would write a
 * credential into AsyncStorage.
 */

/** @typedef {{method: string, url: string, data?: unknown, params?: unknown}} ReplayableRequestConfig */

/**
 * Attach a replayable request config to an axios error, in place. Safe to call on anything.
 * Never overwrites a config the typed client already attached, and never throws — it runs inside
 * a response interceptor, where a throw would replace the real failure with this one.
 *
 * @param {unknown} error
 */
export function attachReplayableRequest(error) {
  if (!error || typeof error !== 'object') return;
  if (error.requestConfig) return;

  const config = error.config;
  if (!config || typeof config !== 'object') return;
  if (typeof config.url !== 'string' || typeof config.method !== 'string') return;

  // `deriveDocumentId` reads `data.firestoreId` off an OBJECT body, and both it and the GET
  // refusal compare an upper-case verb. Axios stores the verb lower-case and may hold the body
  // already serialised, so a verbatim copy would be refused one rule later instead of replayed.
  let data = config.data;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch {
      // Not JSON (FormData, plain text). Keep it verbatim — the replay still re-sends what
      // failed; only the document-identity derivation is unavailable, which the outbox reports
      // for itself rather than having it silently swallowed here.
    }
  }

  error.requestConfig = {
    method: config.method.toUpperCase(),
    url: config.url,
    data,
    params: config.params,
  };
}
